import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../Servicios/supabaseClient.js';
import { Button, Card, Field } from '../../Components/ui/ui.jsx';
import { formatDate, initials } from './format.js';

const EMPTY = { alias: '', email: '' };

export default function Team() {
    const { user, notify } = useOutletContext();

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [confirmingId, setConfirmingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const fetchMembers = useCallback(async () => {
        const { data, error } = await supabase
            .from('authorized_emails')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
        if (error) {
            console.error('Error cargando el team:', error.message);
            notify('No pudimos cargar el team. Recargá la página.', 'danger');
        } else {
            setMembers(data || []);
        }
        setLoading(false);
    }, [user, notify]);

    useEffect(() => { fetchMembers(); }, [fetchMembers]);

    const startEdit = (m) => {
        setEditingId(m.id);
        setConfirmingId(null);
        setForm({ alias: m.alias || '', email: m.email });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setForm(EMPTY);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const email = form.email.toLowerCase().trim();
        if (!email.includes('@')) {
            notify('Revisá el email: parece incompleto.', 'danger');
            return;
        }

        setSaving(true);
        try {
            if (editingId) {
                const { error } = await supabase
                    .from('authorized_emails')
                    .update({ email, alias: form.alias })
                    .eq('id', editingId);
                if (error) throw error;
                notify('Miembro actualizado.');
            } else {
                const { error } = await supabase
                    .from('authorized_emails')
                    .insert([{ user_id: user.id, email, alias: form.alias }]);
                if (error) throw error;
                notify(`${form.alias || email} ya tiene acceso al dashboard.`);
            }
            cancelEdit();
            fetchMembers();
        } catch (err) {
            if (err.code === '23505') {
                notify('Ese email ya tiene acceso.', 'warn');
            } else {
                notify(`No pudimos guardar: ${err.message}`, 'danger');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (m) => {
        const { error } = await supabase.from('authorized_emails').delete().eq('id', m.id);
        if (error) {
            notify(`No pudimos quitar a ${m.alias || m.email}: ${error.message}`, 'danger');
            return;
        }
        setMembers((list) => list.filter((x) => x.id !== m.id));
        setConfirmingId(null);
        if (editingId === m.id) cancelEdit();
        notify(`${m.alias || m.email} ya no tiene acceso.`);
    };

    return (
        <div className="page">
            <header className="pageHeaderText">
                <h1 className="pageTitle">Team</h1>
                <p className="pageSubtitle">Las personas de esta lista pueden entrar al dashboard con su email.</p>
            </header>

            <div className="split">
                <Card
                    title="Miembros"
                    meta={loading ? undefined : `${members.length} ${members.length === 1 ? 'persona' : 'personas'}`}
                >
                    <div className="tableWrap">
                        <table className="dataTable">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    <th>Email</th>
                                    <th>Agregado</th>
                                    <th><span className="srOnly">Acciones</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {members.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="emptyRow">
                                            {loading ? 'Cargando…' : 'Todavía no sumaste a nadie. Agregá a la primera persona desde el formulario.'}
                                        </td>
                                    </tr>
                                ) : members.map((m) => {
                                    const confirming = confirmingId === m.id;
                                    return (
                                        <tr key={m.id} className={confirming ? 'rowDanger' : editingId === m.id ? 'rowEditing' : undefined}>
                                            <td>
                                                <div className="sidebarUserRow">
                                                    <span className="avatar avatarBlue" aria-hidden="true">{initials(m.alias || m.email)}</span>
                                                    <span className="strong">{m.alias || 'Sin nombre'}</span>
                                                </div>
                                            </td>
                                            <td className="muted">{m.email}</td>
                                            <td className="muted num nowrap">{formatDate(m.created_at)}</td>
                                            <td>
                                                <div className="cellActions">
                                                    {confirming ? (
                                                        <>
                                                            <span className="confirmText">¿Quitarle el acceso?</span>
                                                            <button type="button" className="txtBtn" onClick={() => setConfirmingId(null)}>Cancelar</button>
                                                            <Button size="sm" variant="danger" onClick={() => handleDelete(m)}>Quitar</Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button type="button" className="txtBtn txtAccent" onClick={() => startEdit(m)}>Editar</button>
                                                            <button type="button" className="txtBtn txtDanger" onClick={() => setConfirmingId(m.id)}>Quitar</button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>

                <Card featured>
                    <div className="cellStack">
                        <h2 className="qn-card-title">{editingId ? 'Editar miembro' : 'Agregar miembro'}</h2>
                        <p className="cardText">
                            {editingId
                                ? 'Cambiá el nombre o el email. El acceso se actualiza al instante.'
                                : 'Va a poder entrar al dashboard con este email. Podés quitarle el acceso cuando quieras.'}
                        </p>
                    </div>
                    <form className="form" onSubmit={handleSubmit}>
                        <Field
                            id="team-alias"
                            label="Nombre"
                            placeholder="Ej: Juan Pérez"
                            required
                            value={form.alias}
                            onChange={(e) => setForm({ ...form, alias: e.target.value })}
                        />
                        <Field
                            id="team-email"
                            label="Email"
                            type="email"
                            placeholder="juan@empresa.com"
                            hint="Tiene que ser el mismo con el que va a iniciar sesión."
                            required
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                        />
                        <div className="formActions formActionsFill">
                            {editingId && <Button onClick={cancelEdit} disabled={saving}>Cancelar</Button>}
                            <Button type="submit" variant="primary" disabled={saving}>
                                {saving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Agregar al team'}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
}
